import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Group, GroupMember } from '../../models/group.model';
import { TEXT_CONSTANTS } from '../../constants/text-constants';

@Component({
  selector: 'group-share-addon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './group-share-addon.component.html',
})
export class GroupShareAddonComponent implements OnInit {
  TEXT = TEXT_CONSTANTS;

  @Input() groups: Group[] = [];
  @Input() selectedGroupId: string | null = null;
  @Input() defaultGroupId: string | null = null;
  @Input() members: GroupMember[] = [];
  @Input() splitOptions: { key: string; label: string }[] = [];
  @Input() splitType: string = 'equal';
  @Input() paidById: string | null = null;
  @Input() splitValues: { [memberId: string]: number } = {};
  @Input() totalAmount: number = 0;
  @Input() memberSelectedState: { [memberId: string]: boolean } = {};
  @Input() initiallyExpanded = true;
  @Input() showMemberCheckboxes = false;
  /** Toggle "Marcar como deuda" (TAR-325 Req 8). Hidden for "Todo en común" groups. */
  @Input() generatesDebt = true;
  /** Whether the user opted into "Cambiar solo para este gasto" for an income/fixed-division group. */
  @Input() splitOverrideEnabled = false;
  /**
   * The backend only auto-computes splits server-side for transactions
   * (income/fixed-division groups) — fixed expenses always need a manually
   * configured split_type/split_config regardless of the group's division
   * (no SplitService wiring exists for them). So the "división automática /
   * anular para este gasto" behavior only applies in 'transaction' context;
   * in 'fixed-expense' context the split picker always behaves as before.
   */
  @Input() context: 'transaction' | 'fixed-expense' = 'transaction';

  @Output() groupSelected = new EventEmitter<string>();
  @Output() splitTypeChanged = new EventEmitter<string>();
  @Output() paidByChanged = new EventEmitter<string>();
  @Output() splitValueChanged = new EventEmitter<{ memberId: string; value: number }>();
  @Output() memberToggled = new EventEmitter<string>();
  @Output() removed = new EventEmitter<void>();
  @Output() setAsDefault = new EventEmitter<{ groupId: string; isDefault: boolean }>();
  @Output() generatesDebtChanged = new EventEmitter<boolean>();
  @Output() splitOverrideToggled = new EventEmitter<boolean>();
  @Output() openIncomeModal = new EventEmitter<void>();

  isExpanded = true;
  groupPickerOpen = false;

  ngOnInit(): void {
    const isDefaultPreselected = !!this.selectedGroupId && this.selectedGroupId === this.defaultGroupId;
    this.isExpanded = isDefaultPreselected ? false : this.initiallyExpanded;
  }

  get selectedGroup(): Group | undefined {
    return this.groups.find(g => g.id === this.selectedGroupId);
  }

  get selectedGroupIsDefault(): boolean {
    return !!this.selectedGroupId && this.selectedGroupId === this.defaultGroupId;
  }

  onToggleDefault(): void {
    if (this.selectedGroupId) {
      this.setAsDefault.emit({ groupId: this.selectedGroupId, isDefault: !this.selectedGroupIsDefault });
    }
  }

  get collapsedLabel(): string {
    const group = this.selectedGroup;
    if (!group) return this.TEXT.FIXED_EXPENSE_FORM.ADD_ON.SHARE;
    if (this.isCommonDivision) return `Compartido con ${group.name} · En común`;
    if (this.isAutoSplitDivision && !this.splitOverrideEnabled) return `Compartido con ${group.name} · División automática`;
    const splitOpt = this.splitOptions.find(o => o.key === this.splitType);
    return `Compartido con ${group.name} · ${splitOpt?.label ?? ''}`;
  }

  get isEqualSplit(): boolean {
    return this.splitType.toLowerCase() === 'equal';
  }

  get showSplitConfig(): boolean {
    return !this.isEqualSplit;
  }

  // TAR-325: división del grupo seleccionado, usada para decidir qué
  // controles mostrar (el backend ya resuelve el split server-side para
  // "income"/"fixed" salvo que el usuario elija anular por este gasto).
  get isCommonDivision(): boolean {
    return this.selectedGroup?.division === 'common';
  }

  get isAutoSplitDivision(): boolean {
    if (this.context !== 'transaction') return false;
    return this.selectedGroup?.division === 'income' || this.selectedGroup?.division === 'fixed';
  }

  /** El toggle "Marcar como deuda" nunca aplica a grupos "Todo en común" (Req 8.7). */
  get showGeneratesDebtToggle(): boolean {
    return !!this.selectedGroupId && !this.isCommonDivision;
  }

  /** Para "Según ingresos"/"Porcentaje fijo" el split se calcula en el backend
   * por defecto — el picker manual solo se muestra si el usuario elige
   * anularlo para este gasto puntual (Req 4.7). Para "Todo en común" no hay
   * split en absoluto. */
  get showSplitPicker(): boolean {
    return !this.isCommonDivision && (!this.isAutoSplitDivision || this.splitOverrideEnabled);
  }

  /** "Todo en común": no se reparte, solo se registra quién lo cargó. */
  get showPayerOnly(): boolean {
    return this.isCommonDivision;
  }

  private sumOtherMembers(excludeMemberId: string): number {
    return this.members
      .filter(m => m.id !== this.paidById && m.id !== excludeMemberId)
      .reduce((acc, m) => acc + (this.splitValues[m.id] || 0), 0);
  }

  get paidByRemainingAmount(): number {
    const sumOthers = this.members
      .filter(m => m.id !== this.paidById)
      .reduce((acc, m) => acc + (this.splitValues[m.id] || 0), 0);
    return Math.max(0, this.totalAmount - sumOthers);
  }

  get paidByRemainingPercentage(): number {
    const sumOthers = this.members
      .filter(m => m.id !== this.paidById)
      .reduce((acc, m) => acc + (this.splitValues[m.id] || 0), 0);
    return Math.max(0, 100 - sumOthers);
  }

  onSplitInput(memberId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = parseFloat(input.value) || 0;
    const isPercentage = this.splitType.toUpperCase() === 'PERCENTAGE';
    const max = isPercentage ? 100 : this.totalAmount;
    const otherSum = this.sumOtherMembers(memberId);
    if (val + otherSum > max) {
      val = Math.max(0, max - otherSum);
      input.value = val.toString();
    }
    this.splitValueChanged.emit({ memberId, value: val });
  }

  selectGroup(groupId: string): void {
    this.groupPickerOpen = false;
    this.groupSelected.emit(groupId);
  }

  isMemberSelected(memberId: string): boolean {
    return this.memberSelectedState[memberId] !== false;
  }

  getMemberInitials(name: string | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  getMemberAvatarColor(name: string | undefined): string {
    if (!name) return 'bg-zinc-300';
    const colors = ['bg-violet-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-indigo-500'];
    return colors[(name.charCodeAt(0) || 0) % colors.length];
  }
}
